import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

export type BadgeTone =
  | "primary"
  | "brand"
  | "secondary"
  | "neutral"
  | "outline"
  | "scheduled"
  | "completed"
  | "rescheduled"
  | "swap"
  | "replacement"
  | "spontaneous"
  | "cancelled"
  | "warning"
  | "error";

const tones: Record<BadgeTone, string> = {
  primary: "bg-primary-fixed text-primary",
  brand: "bg-primary-container text-on-primary",
  secondary: "bg-secondary-fixed text-on-secondary-fixed-variant",
  neutral: "bg-surface-container-high text-on-surface-variant",
  outline: "bg-surface-container-lowest text-on-surface-variant ring-1 ring-inset ring-outline-variant",
  scheduled: "bg-scheduled-soft text-scheduled",
  completed: "bg-completed-soft text-completed",
  rescheduled: "bg-rescheduled-soft text-rescheduled",
  swap: "bg-swap-soft text-swap",
  replacement: "bg-replacement-soft text-replacement",
  spontaneous: "bg-spontaneous-soft text-spontaneous",
  cancelled: "bg-cancelled-soft text-cancelled",
  warning: "bg-rescheduled-soft text-rescheduled",
  error: "bg-error-container text-on-error-container",
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  icon?: ReactNode;
  size?: "xs" | "sm" | "md";
  shape?: "pill" | "tag";
  dot?: boolean;
}

const sizes = {
  xs: "h-5 gap-1 px-1.5 text-[10px] font-bold [&_svg]:size-3",
  sm: "h-6 gap-1 px-2 text-label-sm font-semibold [&_svg]:size-3.5",
  md: "h-7 gap-1.5 px-2.5 text-label-md [&_svg]:size-4",
};

export function Badge({ tone = "neutral", icon, size = "sm", shape = "pill", dot, className, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap leading-none",
        shape === "pill" ? "rounded-full" : "rounded-md",
        sizes[size],
        tones[tone],
        className,
      )}
      {...props}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" aria-hidden />}
      {icon}
      {children}
    </span>
  );
}
